import React, { useState } from "react";
import { Edit3, ChevronDown, ChevronUp } from "lucide-react"; // Icons for Edit and Expand/Collapse
import AddWorkOrderModal from "../components/AddWorkOrderModal";
import EditWorkOrderModal from "../components/EditWorkOrderModal";

const initialWorkOrders = [
  {
    id: 1,
    name: "Curtains Installation",
    salesId: "SALE001",
    product: "Curtains",
    priority: "High",
    status: "In Progress",
    dueDate: "2025-01-20",
    assignedTeam: "Installation Team",
    progress: 50,
    tasks: [
      { id: 1, name: "Measure Window Dimensions", status: "Completed", dependency: null },
      { id: 2, name: "Procure Materials", status: "In Progress", dependency: 1 },
      { id: 3, name: "Install Curtains", status: "Pending", dependency: 2 },
    ],
  },
  {
    id: 2,
    name: "Office Furnishing",
    salesId: "SALE002",
    product: "Office Furniture",
    priority: "Medium",
    status: "Pending",
    dueDate: "2025-01-25",
    assignedTeam: "Furniture Team",
    progress: 20,
    tasks: [
      { id: 1, name: "Design Layout", status: "Pending", dependency: null },
      { id: 2, name: "Procure Furniture", status: "Pending", dependency: 1 },
      { id: 3, name: "Assemble Furniture", status: "Pending", dependency: 2 },
    ],
  },
];

function WorkOrdersDashboard() {
  const [workOrders, setWorkOrders] = useState(initialWorkOrders);
  const [search, setSearch] = useState("");
  const [priorityFilter, setPriorityFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [expandedRow, setExpandedRow] = useState(null);
  const [isAdding, setIsAdding] = useState(false);
  const [editingWorkOrder, setEditingWorkOrder] = useState(null);

  const filteredWorkOrders = workOrders
    .filter((order) => order.name.toLowerCase().includes(search.toLowerCase()))
    .filter((order) => (priorityFilter ? order.priority === priorityFilter : true))
    .filter((order) => (statusFilter ? order.status === statusFilter : true));

  const handleRowExpand = (id) => setExpandedRow(expandedRow === id ? null : id);

  const handleAddWorkOrder = (newWorkOrder) => {
    setWorkOrders([...workOrders, { ...newWorkOrder, id: workOrders.length + 1 }]);
    setIsAdding(false);
  };

  const handleEditWorkOrder = (updatedWorkOrder) => {
    setWorkOrders(
      workOrders.map((order) => (order.id === updatedWorkOrder.id ? updatedWorkOrder : order))
    );
    setEditingWorkOrder(null);
  };

  return (
    <div className="p-6 bg-gray-100 min-h-screen overflow-auto">
      <div className="flex flex-col md:flex-row justify-between items-center mb-4">
  <h2 className="text-lg md:text-2xl font-semibold text-gray-800 mb-2 md:mb-0">
    Work Orders
  </h2>
  <button
    onClick={() => setIsAdding(true)}
    className="bg-blue-600 text-white px-3 py-2 text-sm rounded-md hover:bg-blue-500"
  >
    + Add Work Order
  </button>
</div>

      {/* Add and Edit Modals */}
      {isAdding && (
        <AddWorkOrderModal
          onClose={() => setIsAdding(false)}
          onSave={handleAddWorkOrder}
        />
      )}
      {editingWorkOrder && (
        <EditWorkOrderModal
          workOrder={editingWorkOrder}
          onClose={() => setEditingWorkOrder(null)}
          onSave={handleEditWorkOrder}
        />
      )}

      {/* Search and Filters */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
        <input
          type="text"
          placeholder="Search work orders..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full max-w-md p-2 border rounded shadow-sm"
        />
        <select
          className="p-2 border rounded shadow-sm"
          value={priorityFilter}
          onChange={(e) => setPriorityFilter(e.target.value)}
        >
          <option value="">All Priorities</option>
          <option value="High">High</option>
          <option value="Medium">Medium</option>
          <option value="Low">Low</option>
        </select>
        <select
          className="p-2 border rounded shadow-sm"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
        >
          <option value="">All Statuses</option>
          <option value="Pending">Pending</option>
          <option value="In Progress">In Progress</option>
          <option value="Completed">Completed</option>
        </select>

      </div>

      {/* Work Orders Table */}
      <div className="flex-1 overflow-y-auto scrollbar-thin scrollbar-thumb-gray-400 scrollbar-track-gray-200 pb-12">        
      <table className="min-w-full table-auto text-sm">
          <thead>
            <tr className="bg-gray-200 text-gray-700">
              <th className="p-4">Work Order Name</th>
              <th className="p-4">Sales ID</th>
              <th className="p-4">Product</th>
              <th className="p-4">Priority</th>
              <th className="p-4">Status</th>
              <th className="p-4">Due Date</th>
              <th className="p-4">Assigned Team</th>
              <th className="p-4">Progress</th>
              <th className="p-4">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredWorkOrders.map((order) => (
              <React.Fragment key={order.id}>
                {/* Work Order Row */}
                <tr className="border-t hover:bg-gray-50 transition duration-150">
                <td className="px-4 py-2 truncate max-w-[150px]">{order.name}</td>
<td className="px-4 py-2 truncate max-w-[100px]">
  <a href={`/sales/${order.salesId}`} className="text-blue-600 hover:underline">
    {order.salesId}
  </a>
</td>
<td className="px-4 py-2 truncate max-w-[120px]">{order.product}</td>
<td className="px-4 py-2 text-center">
  <span className={`px-2 py-1 rounded-full text-sm ${
    order.priority === "High"
      ? "bg-red-100 text-red-600"
      : order.priority === "Medium"
      ? "bg-yellow-100 text-yellow-600"
      : "bg-green-100 text-green-600"
  }`}>
    {order.priority}
  </span>
</td>
<td className="px-4 py-2 text-center">{order.status}</td>
<td className="px-4 py-2 text-center">{order.dueDate}</td>
<td className="px-4 py-2 truncate max-w-[150px]">{order.assignedTeam}</td>
<td className="px-4 py-2 text-center">
  <div className="w-full bg-gray-200 rounded-full h-4">
    <div className={`h-4 rounded-full ${
      order.progress < 50 ? "bg-red-500" : order.progress < 80 ? "bg-yellow-500" : "bg-green-500"
    }`} style={{ width: `${order.progress}%` }}></div>
  </div>
</td>
                  <td className="px-2 py-2 flex items-center justify-center space-x-1">
  <button onClick={() => setEditingWorkOrder(order)} className="text-blue-600 hover:text-blue-800 p-1">
    <Edit3 size={14} />
  </button>
  <button onClick={() => handleRowExpand(order.id)} className="text-gray-600 hover:text-gray-800 p-1">
    {expandedRow === order.id ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
  </button>
</td>
                </tr>

                {/* Expanded Tasks Row */}
                {expandedRow === order.id && (
                  <tr className="bg-gray-50">
                    <td colSpan={9} className="p-4">
                      <h3 className="text-lg font-bold text-gray-800 mb-2">
                        Tasks for {order.name}
                      </h3>
                      <ul className="space-y-2">
                        {order.tasks.map((task) => (
                          <li
                            key={task.id}
                            className="flex justify-between items-center"
                          >
                            <span>{task.name}</span>
                            <span
                              className={`px-2 py-1 rounded-full text-sm ${
                                task.status === "Completed"
                                  ? "bg-green-100 text-green-600"
                                  : task.status === "In Progress"
                                  ? "bg-yellow-100 text-yellow-600"
                                  : "bg-red-100 text-red-600"
                              }`}
                            >
                              {task.status}
                            </span>
                          </li>
                        ))}
                      </ul>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default WorkOrdersDashboard;